import { useMemo, useState } from "react";
import { useOutletContext } from "react-router";
import { DataTable } from "primereact/datatable";
import type { DataTableValueArray } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { SplitButton } from "primereact/splitbutton";
import { formatShortDate, formatTimeToComplete } from "sj-ui-toolkit/src/utils/dates";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils/user";
import { downloadPathCertificate } from "sj-ui-toolkit/src/api";
import { getRailsClient } from "sj-ui-toolkit/src/client";
import { useToast } from "sj-ui-toolkit/src/hooks";

import { skeletonRows } from "@/helpers/tableBody";
import type { UserData } from "@/pages/Users";
import type { Belt } from "@/api/users";

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CertificateInfoColumn({ rowData, isLoading }: { rowData: Belt; isLoading: boolean }) {
  if (isLoading) return <Skeleton />;
  return (
    <div className="flex flex-col">
      <span className="body-md">
        {rowData.levelName}: {rowData.name}
      </span>
      <span className="body-sm text-muted">
        Earned: {formatShortDate(rowData.completedAt ?? "")}
      </span>
    </div>
  );
}

function PathDurationColumn({ rowData, isLoading }: { rowData: Belt; isLoading: boolean }) {
  if (isLoading) return <Skeleton />;
  return (
    <div className="flex flex-col body-sm">
      <span className="text-muted">Path Duration</span>
      <span>
        <i className="pi pi-clock body-xs mr-2" aria-hidden="true" />
        {formatTimeToComplete(rowData.timeSpent ?? "0")}
      </span>
    </div>
  );
}

function CompletedCountColumn({ rowData, isLoading }: { rowData: Belt; isLoading: boolean }) {
  if (isLoading) return <Skeleton />;
  const count = rowData.lessonsCompleteCount ?? 0;
  return (
    <div className="flex flex-col body-sm">
      <span className="text-muted">Completed</span>
      <span className="flex">
        <i className="pi pi-stack body-xs mr-2" aria-hidden="true" />
        {count} {count === 1 ? "lesson" : "lessons"}
      </span>
    </div>
  );
}

function DownloadCertificateColumn({
  rowData,
  isLoading,
  userId,
}: {
  rowData: Belt;
  isLoading: boolean;
  userId: string | undefined;
}) {
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);
  const { showToast } = useToast();

  if (isLoading) return <Skeleton />;

  const handleDownload = async () => {
    if (!rowData.roleId || !userId) {
      showToast({ severity: "error", detail: "Cannot download: missing certificate or user data" });
      return;
    }
    setIsRequestInProgress(true);
    try {
      await downloadPathCertificate(Number(rowData.roleId), Number(userId));
    } catch {
      showToast({ severity: "error", detail: "Failed to download certificate" });
    } finally {
      setIsRequestInProgress(false);
    }
  };

  const handleView = () => {
    const tenant = getCurrentSelectedTenant();
    if (!tenant || !rowData.roleEnrollmentPublicId) return;
    window.open(
      `/api/certificates/${rowData.roleEnrollmentPublicId}/${tenant}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleSendEmail = async () => {
    if (!rowData.emailUrl || !userId) return;
    setIsRequestInProgress(true);
    try {
      await getRailsClient().get(rowData.emailUrl, { params: { userId } });
      showToast({ severity: "success", detail: "Certificate email sent successfully" });
    } catch {
      showToast({ severity: "error", detail: "Failed to send certificate email" });
    } finally {
      setIsRequestInProgress(false);
    }
  };

  return (
    <SplitButton
      label="Download"
      icon="pi pi-download"
      onClick={() => void handleDownload()}
      model={[
        { label: "View Certificate", command: handleView },
        { label: "Send to Email", command: () => void handleSendEmail() },
      ]}
      loading={isRequestInProgress}
      disabled={isRequestInProgress || !userId}
    />
  );
}

// ---------------------------------------------------------------------------
// Tab component
// ---------------------------------------------------------------------------

export default function UserCertificatesTab() {
  const { user, isLoading } = useOutletContext<{ user: UserData | null; isLoading: boolean }>();

  const passedBelts = user?.passedBelts ?? [];
  const skeletonData = useMemo(() => skeletonRows<Belt>(10), []);

  return (
    <div>
      <h2 className="atlas-h2 mb-4">Certificates</h2>
      <DataTable<Belt[]>
        value={(isLoading ? skeletonData : passedBelts) as unknown as DataTableValueArray}
        paginator
        aria-label="User certificates"
        showHeaders={false}
        rows={10}
        rowsPerPageOptions={[10, 20, 50]}
        emptyMessage="No certificates available."
        tableClassName="dt-responsive-text"
      >
        <Column
          header="Certificate"
          field="name"
          body={(row: Belt) => <CertificateInfoColumn rowData={row} isLoading={isLoading} />}
        />
        <Column
          header="Path Duration"
          field="timeSpent"
          body={(row: Belt) => <PathDurationColumn rowData={row} isLoading={isLoading} />}
        />
        <Column
          header="Lessons Completed"
          field="lessonsCompleteCount"
          body={(row: Belt) => <CompletedCountColumn rowData={row} isLoading={isLoading} />}
        />
        <Column
          header="Actions"
          body={(row: Belt) => (
            <DownloadCertificateColumn rowData={row} isLoading={isLoading} userId={user?.id} />
          )}
        />
      </DataTable>
    </div>
  );
}
